import { differenceInDays } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { parseDateOnly } from '@/utils/dateUtils';
import type { AgingBasis } from '@erp/rpc';
import type { MultiSelectOption } from '@/components/common/MultiSelect';
import type { ArReportFilters } from '@/components/accounts-receivable/ar-report-filters';

// ─── Types ───────────────────────────────────────────────────────────────────

export type Basis =
  'days_overdue' | 'days_since_invoice' | 'days_since_shipment';
export type ViewMode = 'grouped' | 'flat' | 'grouped_detail';

export interface CustomerOption extends MultiSelectOption {
  id: string;
  label: string;
}

export const STATUS_OPTIONS: MultiSelectOption[] = [
  { id: 'draft', label: 'Draft' },
  { id: 'open', label: 'Open' },
  { id: 'closed', label: 'Closed' },
  { id: 'voided', label: 'Voided' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Days the invoice has been outstanding under the current aging basis.
 * Mirrors the backend computation in `ar-custom-aging.handlers.ts` —
 * keep the two in sync.
 */
export function getDaysOut(
  invoice: {
    dueDate: string | null;
    invoiceDate: string | null;
    shipDate: string | null;
  },
  basis: AgingBasis,
): number | null {
  const basisDate =
    basis === 'days_overdue'
      ? invoice.dueDate
      : basis === 'days_since_shipment'
        ? (invoice.shipDate ?? invoice.invoiceDate)
        : invoice.invoiceDate;
  if (basisDate == null) return null;
  const parsed = parseDateOnly(basisDate);
  if (parsed == null) return null;
  return differenceInDays(new Date(), parsed);
}

export function buildRangeChipLabel(
  settings: ArReportFilters,
  activePreset: string | null,
): string {
  const basisLabel =
    settings.basis === 'days_overdue'
      ? 'Days Overdue'
      : settings.basis === 'days_since_invoice'
        ? 'Days Since Invoice'
        : 'Days Since Shipment';
  if (activePreset) {
    return `${activePreset} ${basisLabel}`;
  }
  if (settings.currentOnly) {
    return `Current ${basisLabel}`;
  }
  const rangeStr =
    settings.maxDays !== undefined
      ? `${settings.minDays}–${settings.maxDays}`
      : `${settings.minDays}+`;
  return `Custom: ${rangeStr} ${basisLabel}`;
}

// ─── Active filter chip summary ──────────────────────────────────────────────

interface ActiveFilterSummaryProps {
  settings: ArReportFilters;
  activePreset: string | null;
  selectedCustomers: CustomerOption[];
  selectedStatuses: MultiSelectOption[];
  lastSentMinDays: number | undefined;
  lastSentMaxDays: number | undefined;
  includeNeverSent: boolean;
}

function buildLastSentChipLabel(
  minDays: number | undefined,
  maxDays: number | undefined,
  includeNeverSent: boolean,
): string | null {
  const hasMin = minDays !== undefined;
  const hasMax = maxDays !== undefined;
  if (!hasMin && !hasMax && !includeNeverSent) return null;

  const parts: string[] = [];
  if (hasMin) parts.push(`Not sent in ${minDays}d`);
  if (hasMax) parts.push(`Sent within ${maxDays}d`);
  if (includeNeverSent) {
    parts.push(parts.length === 0 ? 'Never sent' : 'or never');
  }
  return parts.join(' · ');
}

export function ActiveFilterSummary({
  settings,
  activePreset,
  selectedCustomers,
  selectedStatuses,
  lastSentMinDays,
  lastSentMaxDays,
  includeNeverSent,
}: ActiveFilterSummaryProps) {
  const rangeLabel = buildRangeChipLabel(settings, activePreset);
  const lastSentLabel = buildLastSentChipLabel(
    lastSentMinDays,
    lastSentMaxDays,
    includeNeverSent,
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="secondary" className="text-xs">
        {rangeLabel}
      </Badge>

      {selectedCustomers.length > 0 && (
        <Badge variant="secondary" className="text-xs">
          {selectedCustomers.length === 1
            ? `Customer: ${selectedCustomers[0].label}`
            : `Customers: ${selectedCustomers[0].label}${selectedCustomers.length > 1 ? `, +${selectedCustomers.length - 1} more` : ''}`}
        </Badge>
      )}

      {selectedStatuses.length > 0 && (
        <Badge variant="secondary" className="text-xs">
          {`Status: ${selectedStatuses.map((s) => s.label).join(', ')}`}
        </Badge>
      )}

      {lastSentLabel !== null && (
        <Badge variant="secondary" className="text-xs">
          {lastSentLabel}
        </Badge>
      )}
    </div>
  );
}
